import React, { useEffect, useRef, useState } from "react";

// --- Luxury Black & Gold Google Maps dark style ---
const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0b0f17" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0b0f17" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a8f9c" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#2a2f3a" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#b7bcc8" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#0b0f17" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#374151" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#111827" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#e5c56a" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#060a12" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4b5563" }] },
];

// Singleton loader with async importLibrary pattern (new Google Maps JS SDK)
let googleMapsLoader = null;
export function loadGoogleMaps(apiKey) {
  if (googleMapsLoader) return googleMapsLoader;
  googleMapsLoader = new Promise((resolve, reject) => {
    if (!apiKey) { reject(new Error("GOOGLE_MAPS_API_KEY mancante")); return; }
    const scriptId = "gmaps-js-sdk";
    const start = async () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
          throw new Error("Google Maps SDK non pronto");
        }
        // Importing libraries attaches tutti i costruttori anche su window.google.maps
        await Promise.all([
          window.google.maps.importLibrary("maps"),
          window.google.maps.importLibrary("core"),
          window.google.maps.importLibrary("marker"),
        ]);
        const gmaps = window.google.maps;
        resolve({
          Map: gmaps.Map,
          Polyline: gmaps.Polyline,
          LatLngBounds: gmaps.LatLngBounds,
          Marker: gmaps.Marker,
          event: gmaps.event,
        });
      } catch (e) {
        reject(e);
      }
    };
    const existing = document.getElementById(scriptId);
    if (existing) {
      if (window.google && window.google.maps) start();
      else {
        existing.addEventListener("load", start, { once: true });
        existing.addEventListener("error", () => reject(new Error("Caricamento Google Maps fallito")), { once: true });
      }
      return;
    }
    const s = document.createElement("script");
    s.id = scriptId;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&libraries=marker&language=it&region=IT&loading=async&callback=__gmapsReady__`;
    window.__gmapsReady__ = start;
    s.onerror = () => reject(new Error("Caricamento Google Maps fallito"));
    document.head.appendChild(s);
  });
  return googleMapsLoader;
}

function pinSvg(color) {
  // Luxury pin: gold-bordered color teardrop
  return {
    path: "M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#F5CB5C",
    strokeWeight: 2,
    scale: 1,
    anchor: { x: 12, y: 36 },
  };
}

export default function MapPreview({ from, to, geometry }) {
  const nodeRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Init map once
  useEffect(() => {
    let cancelled = false;
    if (!nodeRef.current) return;
    loadGoogleMaps(apiKey)
      .then((gm) => {
        if (cancelled || !nodeRef.current) return;
        const map = new gm.Map(nodeRef.current, {
          center: { lat: 41.9028, lng: 12.4964 },
          zoom: 6,
          disableDefaultUI: false,
          clickableIcons: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          backgroundColor: "#0b0f17",
          styles: DARK_STYLE,
        });
        mapRef.current = { map, gm };
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Errore Google Maps");
      });
    return () => {
      cancelled = true;
      overlaysRef.current.forEach((o) => o.setMap && o.setMap(null));
      overlaysRef.current = [];
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render markers + polyline when inputs change
  useEffect(() => {
    const ref = mapRef.current;
    if (!ref || !ref.map || !ref.gm) return;
    const { map, gm } = ref;
    // clear previous overlays
    overlaysRef.current.forEach((o) => o.setMap && o.setMap(null));
    overlaysRef.current = [];
    if (!from?.lat || !to?.lat) return;

    const startLatLng = { lat: Number(from.lat), lng: Number(from.lon) };
    const endLatLng = { lat: Number(to.lat), lng: Number(to.lon) };

    // Classic Marker: funziona con styles custom (AdvancedMarkerElement richiederebbe mapId)
    const startMarker = new gm.Marker({ position: startLatLng, map, title: "Partenza", icon: pinSvg("#10B981"), zIndex: 2 });
    const endMarker = new gm.Marker({ position: endLatLng, map, title: "Destinazione", icon: pinSvg("#F43F5E"), zIndex: 2 });
    overlaysRef.current.push(startMarker, endMarker);

    const bounds = new gm.LatLngBounds();
    bounds.extend(startLatLng);
    bounds.extend(endLatLng);

    if (geometry && Array.isArray(geometry) && geometry.length > 1) {
      // backend returns [lon, lat] GeoJSON pairs
      const path = geometry.map((c) => ({ lat: Number(c[1]), lng: Number(c[0]) }));
      const polyline = new gm.Polyline({
        path,
        geodesic: false,
        strokeColor: "#F5CB5C",
        strokeOpacity: 0.95,
        strokeWeight: 5,
        map,
        zIndex: 1,
      });
      overlaysRef.current.push(polyline);
      path.forEach((p) => bounds.extend(p));
    }

    map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    // Clamp zoom (fitBounds on a very short route may zoom too much)
    const listener = gm.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > 16) map.setZoom(16);
    });
    overlaysRef.current.push({ setMap: () => gm.event.removeListener(listener) });
  }, [ready, from?.lat, from?.lon, to?.lat, to?.lon, geometry]);

  if (error) {
    return (
      <div
        className="w-full h-56 rounded-xl border border-rose-500/40 bg-rose-500/5 flex items-center justify-center text-center px-6"
        data-testid="map-preview-error"
      >
        <div className="text-rose-300 text-xs font-mono-num">
          Impossibile caricare la mappa. Verifica la configurazione Google Maps.
          <div className="text-rose-400/60 text-[10px] mt-1 uppercase tracking-widest">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={nodeRef}
        className="w-full h-56 sm:h-64 rounded-xl overflow-hidden border border-amber-500/20 shadow-[0_0_0_1px_rgba(245,203,92,0.08)] bg-[#0b0f17]"
        data-testid="map-preview"
      />
      {(!from?.lat || !to?.lat) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-xs text-slate-400 bg-slate-950/70 rounded-xl">
          Seleziona partenza e destinazione dall'autocomplete per vedere il percorso
        </div>
      )}
    </div>
  );
}
